import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "./ui/sheet";
import { Bookmark, BookmarkX, Heart, Play, ExternalLink, Mic, Music as MusicIcon } from "lucide-react";
import { useBookmarks } from "../hooks/useBookmarks";
import { usePlayer } from "../context/PlayerContext";
import { ShareMyBecoming } from "./ShareMyBecoming";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MyBecoming = () => {
    const { marks, total, remove } = useBookmarks();
    const [open, setOpen] = useState(false);
    const [episodes, setEpisodes] = useState([]);
    const [tracks, setTracks] = useState([]);
    const { playEpisode } = usePlayer();

    // Lazy fetch when sheet opens or bookmarks change
    useEffect(() => {
        if (!open || total === 0) return;
        let mounted = true;
        Promise.all([
            axios.get(`${API}/episodes`).catch(() => ({ data: [] })),
            axios.get(`${API}/music`).catch(() => ({ data: [] })),
        ]).then(([e, m]) => {
            if (!mounted) return;
            setEpisodes(e.data || []);
            setTracks(m.data || []);
        });
        return () => { mounted = false; };
    }, [open, total, marks.episodes.length, marks.tracks.length]);

    const savedEpisodes = episodes.filter((ep) => marks.episodes.includes(ep.id));
    const savedTracks = tracks.filter((t) => marks.tracks.includes(t.id));

    return (
        <>
            {/* Floating launcher */}
            <button
                onClick={() => setOpen(true)}
                data-testid="my-becoming-launcher"
                className={`fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full pl-3 pr-5 h-12 bg-lilac-900 hover:bg-lilac-700 text-white shadow-[0_18px_40px_rgba(43,29,53,0.30)] transition-all ${
                    total === 0 ? "opacity-90" : ""
                }`}
                aria-label="Open My Becoming collection"
            >
                <span className="grid place-items-center w-8 h-8 rounded-full bg-gold/95 text-lilac-900">
                    <Heart className="w-4 h-4" fill={total > 0 ? "currentColor" : "none"} />
                </span>
                <span className="text-sm font-medium">My Becoming</span>
                {total > 0 && (
                    <span
                        data-testid="my-becoming-count"
                        className="ml-1 text-[11px] tabular-nums bg-gold text-lilac-900 rounded-full px-2 py-0.5 font-semibold"
                    >
                        {total}
                    </span>
                )}
            </button>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger className="hidden" />
                <SheetContent
                    side="right"
                    data-testid="my-becoming-sheet"
                    className="w-full sm:max-w-md overflow-y-auto bg-gradient-to-b from-[#FAF5FC] to-[#F4ECF7] border-lilac-200"
                >
                    <SheetHeader>
                        <div className="eyebrow text-gold-deep flex items-center gap-2">
                            <Bookmark className="w-3.5 h-3.5" /> My Becoming
                        </div>
                        <SheetTitle className="font-display text-3xl text-lilac-900 leading-tight">
                            Your sacred collection.
                        </SheetTitle>
                        <SheetDescription className="text-sm text-lilac-700 leading-relaxed">
                            Episodes &amp; songs you've kept close. Saved on this device — no account
                            needed.
                        </SheetDescription>
                    </SheetHeader>

                    <div className="mt-8 space-y-8">
                        {/* Episodes */}
                        <section>
                            <div className="flex items-center gap-2 eyebrow text-lilac-700 mb-3">
                                <Mic className="w-3.5 h-3.5" /> Episodes ({savedEpisodes.length})
                            </div>
                            {savedEpisodes.length === 0 ? (
                                <EmptyHint copy="Tap the heart on any episode to save it here." />
                            ) : (
                                <ul className="space-y-3">
                                    {savedEpisodes.map((ep) => (
                                        <li
                                            key={ep.id}
                                            data-testid={`my-becoming-episode-${ep.number}`}
                                            className="rounded-2xl bg-white border border-border p-3 flex items-center gap-3"
                                        >
                                            <img
                                                src={ep.cover_url}
                                                alt=""
                                                className="w-12 h-12 rounded-xl object-cover"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[11px] uppercase tracking-wider text-lilac-700">
                                                    EP {String(ep.number).padStart(2, "0")} · {ep.theme}
                                                </div>
                                                <div className="font-display text-base text-lilac-900 truncate">
                                                    {ep.title}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                {ep.audio_url && (
                                                    <button
                                                        onClick={() => {
                                                            playEpisode(ep);
                                                            setOpen(false);
                                                        }}
                                                        aria-label="Play"
                                                        className="grid place-items-center w-8 h-8 rounded-full bg-gold text-white"
                                                    >
                                                        <Play className="w-3.5 h-3.5" fill="currentColor" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => remove("episodes", ep.id)}
                                                    aria-label="Remove"
                                                    data-testid={`my-becoming-remove-ep-${ep.number}`}
                                                    className="grid place-items-center w-8 h-8 rounded-full border border-border text-lilac-700 hover:bg-red-50 hover:text-red-600"
                                                >
                                                    <BookmarkX className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        {/* Tracks */}
                        <section>
                            <div className="flex items-center gap-2 eyebrow text-lilac-700 mb-3">
                                <MusicIcon className="w-3.5 h-3.5" /> Music ({savedTracks.length})
                            </div>
                            {savedTracks.length === 0 ? (
                                <EmptyHint copy="Tap the heart on any track to keep it here." />
                            ) : (
                                <ul className="space-y-3">
                                    {savedTracks.map((t) => {
                                        const link = t.spotify_url || t.apple_url || t.youtube_url;
                                        return (
                                            <li
                                                key={t.id}
                                                data-testid={`my-becoming-track-${t.id}`}
                                                className="rounded-2xl bg-white border border-border p-3 flex items-center gap-3"
                                            >
                                                <img
                                                    src={t.cover_url}
                                                    alt=""
                                                    className="w-12 h-12 rounded-xl object-cover"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-[11px] uppercase tracking-wider text-lilac-700">
                                                        {t.track_type || "Album"}
                                                    </div>
                                                    <div className="font-display text-base text-lilac-900 truncate">
                                                        {t.title}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    {link && (
                                                        <a
                                                            href={link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            aria-label="Listen"
                                                            className="grid place-items-center w-8 h-8 rounded-full bg-gold text-white"
                                                        >
                                                            <ExternalLink className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                    <button
                                                        onClick={() => remove("tracks", t.id)}
                                                        aria-label="Remove"
                                                        className="grid place-items-center w-8 h-8 rounded-full border border-border text-lilac-700 hover:bg-red-50 hover:text-red-600"
                                                    >
                                                        <BookmarkX className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </section>

                        <ShareMyBecoming
                            savedEpisodes={savedEpisodes}
                            savedTracks={savedTracks}
                        />
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
};

const EmptyHint = ({ copy }) => (
    <div className="rounded-2xl border border-dashed border-lilac-300 bg-white/40 p-5 text-sm text-lilac-700 leading-relaxed">
        {copy}
    </div>
);
